import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  View,
} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchMyLogsInRange} from '@/entities/me/api';
import {ALL_TIME, periodWindow} from '@/entities/log/day';
import {
  matchPreset,
  parsePeriodKey,
  periodFromPreset,
  periodKey,
  type Period,
  type PeriodPresetId,
} from '@/entities/attendance/period';
import {FilterBar, FilterSelect} from '@/shared/ui/filter-bar';
import type {AccessLogRecord} from '@/entities/log/types';
import {IconBubble} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Icon} from '@/shared/ui/icons';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {Pager} from '@/shared/ui/pager';
import {useContentInsets} from '@/shared/ui/screen';
import {SkeletonList} from '@/shared/ui/skeleton';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {formatDate, formatSimilarity} from '@/shared/lib/format';
import {
  intParam,
  stringParam,
  useParamState,
  type ParamCodec,
} from '@/shared/lib/use-param-state';

/**
 * "Мои проходы" — the employee's own access events. It is the household feed's sibling
 * (view-journal/Journal), with two differences that follow from "always me":
 * no avatar column (there is nothing to recognise — every row is the same person) and
 * no name (the screen already is the person). What is left is what an employee actually
 * checks: which door, when, and was it granted.
 *
 * Paging is 1-based against `total` — /me/logs takes page/per_page, not offset — and
 * the list stops asking once it holds them all.
 *
 * ## Дни разделены заголовками, и это не украшение
 *
 * Раньше строка несла дату сама: `formatEventTime` печатал «14.08 09:14» на всём, что
 * не сегодня. На ленте из шести проходов в день это шесть повторов одной даты подряд —
 * и при этом НИ ОДНОГО ответа на вопрос, который к этой ленте приходят задавать: «а
 * сколько раз я вчера проходил». Заголовок дня печатает дату один раз и считает
 * события под собой; в строке остаётся время, то есть ровно то, чем строки и
 * отличаются друг от друга.
 *
 * Группировка идёт по УЖЕ ЗАГРУЖЕННЫМ страницам, поэтому последний заголовок на
 * экране может быть неполным, пока не подгрузилась следующая страница. Это верно и для
 * счётчика под ним, и это честнее, чем не показывать его вовсе: лента бесконечная, а
 * «сегодня» в ней всегда сверху и всегда целиком.
 */
const PAGE_SIZE = 30;
const PAGE_PARAM = intParam(1);
const PER_PAGE_PARAM = intParam(PAGE_SIZE);
/** Размеры страницы, которые предлагает рейка под лентой. */
const PAGE_SIZES = [30, 60, 100] as const;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "HH:MM" — дата теперь стоит в заголовке дня, и в строке ей делать нечего. */
function clock(millis: number): string {
  const d = new Date(millis);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Локальный календарный день события — ключ группировки. */
function dayKey(millis: number): string {
  const d = new Date(millis);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

type FeedItem =
  | {kind: 'day'; key: string; date: string; millis: number; count: number}
  | {kind: 'event'; key: string; log: AccessLogRecord};

/**
 * Плоский список событий → тот же список с заголовком перед каждым новым днём.
 *
 * Одним проходом и без сортировки: порядок ленты — серверный (новые вперёд), и
 * переставлять его здесь значило бы разойтись с пагинацией, которая считает строки, а
 * не даты.
 */
function withDayHeaders(logs: readonly AccessLogRecord[]): FeedItem[] {
  const items: FeedItem[] = [];
  let current: Extract<FeedItem, {kind: 'day'}> | null = null;

  for (const log of logs) {
    const key = dayKey(log.created_at_millis);
    if (!current || current.key !== key) {
      current = {
        kind: 'day',
        key,
        date: key,
        millis: log.created_at_millis,
        count: 0,
      };
      items.push(current);
    }
    current.count += 1;
    items.push({kind: 'event', key: log.event_id, log});
  }
  return items;
}

const styles = StyleSheet.create({
  frame: {flex: 1},
  filters: {paddingBottom: spacing(3)},
  pager: {paddingHorizontal: spacing(4), paddingVertical: spacing(2)},
  list: {
    gap: spacing(2),
    paddingVertical: spacing(3),
    flexGrow: 1,
    width: '100%',
    alignSelf: 'center',
  },
  dayHead: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing(2),
    paddingTop: spacing(3),
    paddingHorizontal: spacing(1),
  },
  dayCount: {flex: 1, textAlign: 'right'},
  listEmpty: {justifyContent: 'center'},
  footer: {paddingVertical: spacing(5)},

  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.lg,
    overflow: 'hidden',
  },
  rail: {width: 4},
  rowInner: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingVertical: spacing(3),
    paddingHorizontal: spacing(3),
  },
  body: {flex: 1, gap: 2},
  meta: {alignItems: 'flex-end', gap: spacing(1)},
});

/**
 * One of my events. The status rail carries the verdict before any text is read.
 * Memoised for the same reason as the household feed's row: 30 rows per page, all
 * re-rendered by any list-level state change, none of them actually changing.
 */
const MyFeedRow = React.memo(function MyFeedRow({log}: {log: AccessLogRecord}) {
  const {t} = useTranslation();
  const p = useTheme();

  const tone: StatusTone = log.granted ? 'granted' : 'denied';
  const railColor = log.granted ? p.signal : p.destructive;
  const showSimilarity = log.granted && log.similarity_percent != null;

  return (
    <View style={[styles.row, {backgroundColor: p.card, borderColor: p.border}]}>
      <View style={[styles.rail, {backgroundColor: railColor}]} />
      <View style={styles.rowInner}>
        {/* No avatar here — every row is the same person, so the glyph carries the
            DOOR instead, which is the thing that actually differs down the list. */}
        <IconBubble tone={log.granted ? p.signalSoft : p.destructiveSoft} size={40} square>
          <Icon name="devices" size={20} color={railColor} />
        </IconBubble>
        <View style={styles.body}>
          <AppText variant="subtitle" numberOfLines={1}>
            {log.device_name}
          </AppText>
          {log.location_label ? (
            <AppText variant="caption" numberOfLines={1}>
              {log.location_label}
            </AppText>
          ) : null}
        </View>
        <View style={styles.meta}>
          <AppText variant="mono">{clock(log.created_at_millis)}</AppText>
          <StatusPill
            tone={tone}
            size="sm"
            dot={false}
            label={
              showSimilarity
                ? `${t(log.granted ? 'myLogs.granted' : 'myLogs.denied')} · ${formatSimilarity(
                    log.similarity_percent,
                  )}`
                : t(log.granted ? 'myLogs.granted' : 'myLogs.denied')
            }
          />
        </View>
      </View>
    </View>
  );
});

export function MyLogsFeed({companyId}: {companyId: string}) {
  const {t} = useTranslation();
  const p = useTheme();
  // Список владеет скроллом целиком (иначе ломается потянуть-обновить), поэтому
  // боковой отступ он платит сам — страница-хозяин его не платит.
  const insets = useContentInsets();

  const {isDesktop} = useBreakpoint();
  // Период, результат и страница — в адресе: своя лента тоже переживает F5.
  const [perPage, setPerPage] = useParamState('per', PAGE_SIZE, PER_PAGE_PARAM);

  // Сутки закрепляются на монтирование: лента, переехавшая на новый день посреди
  // взгляда, врёт про «сегодня» до следующего обновления.
  const now = useMemo(() => new Date(), []);
  /** `null` — «за всё время». */
  const defaultPeriod = useMemo(() => periodFromPreset('month', now), [now]);
  const periodCodec = useMemo<ParamCodec<Period | null>>(() => {
    const empty = periodKey(defaultPeriod);
    return {
      parse: raw => (raw === 'all' ? null : parsePeriodKey(raw, now)),
      format: value => {
        if (value === null) {
          return 'all';
        }
        const key = periodKey(value);
        return key === empty ? undefined : key;
      },
    };
  }, [defaultPeriod, now]);
  const [period, setPeriod] = useParamState<Period | null>('period', defaultPeriod, periodCodec);
  const [result, setResult] = useParamState('result', '', stringParam);

  /*
    Один сбор за период вместо двух серверных разбивок.

    Раньше здесь была страница на десктопе и бесконечная лента на телефоне — обе
    честно листали и обе делали невозможным отбор: `/me/logs` не принимает ни дат, ни
    результата, ни порядка. Отфильтровать одну серверную страницу и подписать её
    периодом — это то же враньё, что и в журнале компании, только на своих проходах.

    Личное дело маленькое (сотни событий в год), так что месяц собирается за один-два
    запроса.
  */
  const query = useQuery({
    queryKey: ['me-logs-range', companyId, period ? periodKey(period) : 'all'],
    queryFn: () =>
      fetchMyLogsInRange(companyId, period ? periodWindow(period) : ALL_TIME),
  });

  const isPending = query.isPending;
  const isError = query.isError;
  const isRefetching = query.isRefetching;
  const refetch = () => {
    void query.refetch();
  };

  const collected = useMemo(() => query.data?.events ?? [], [query.data]);
  const matched = useMemo(
    () =>
      result === ''
        ? collected
        : collected.filter(e => (result === 'granted' ? e.granted : !e.granted)),
    [collected, result],
  );
  // Собрано по возрастанию времени, а ленту читают от последнего события назад.
  const logs = useMemo(() => [...matched].reverse(), [matched]);
  const total = logs.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const [page, setPage] = useParamState('page', 1, PAGE_PARAM);
  const pageLogs = useMemo(
    () => logs.slice((page - 1) * perPage, page * perPage),
    [logs, page, perPage],
  );

  /* Новый отбор — новая первая страница. Первый прогон пропускается: страница теперь
     живёт в адресе, и сброс на монтировании затирал бы `?page=3` из присланной ссылки. */
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setPage(1);
    // `setPage` стабилен по построению `useParamState`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, companyId, period, result]);

  const filters = (
    <FilterBar testID="my-logs-filters">
      <FilterSelect
        label={t('journal.period')}
        value={period === null ? 'all' : matchPreset(period, now) ?? 'custom'}
        options={[
          {value: 'today', label: t('reports.preset.today')},
          {value: 'week', label: t('reports.preset.week')},
          {value: 'month', label: t('reports.preset.month')},
          {value: 'prevMonth', label: t('reports.preset.prevMonth')},
          {value: 'all', label: t('journal.allTime')},
        ]}
        onChange={next =>
          setPeriod(
            next === 'all' ? null : periodFromPreset(next as PeriodPresetId, now),
          )
        }
        neutral="all"
        phone={!isDesktop}
        testID="my-logs-period"
      />
      <FilterSelect
        label={t('journal.result')}
        value={result}
        options={[
          {value: '', label: t('actions.any')},
          {value: 'granted', label: t('home.feed.granted')},
          {value: 'denied', label: t('home.feed.denied')},
        ]}
        onChange={setResult}
        phone={!isDesktop}
        testID="my-logs-result"
      />
    </FilterBar>
  );

  const items = useMemo(() => withDayHeaders(pageLogs), [pageLogs]);

  // «Сегодня» и «Вчера» читаются быстрее даты, а всё остальное датой и остаётся.
  const today = dayKey(Date.now());
  const yesterday = dayKey(Date.now() - 86_400_000);
  const dayTitle = (item: Extract<FeedItem, {kind: 'day'}>) =>
    item.date === today
      ? t('myLogs.today')
      : item.date === yesterday
      ? t('myLogs.yesterday')
      : formatDate(item.millis);

  const renderItem = useCallback(
    ({item}: {item: FeedItem}) =>
      item.kind === 'event' ? (
        <MyFeedRow log={item.log} />
      ) : (
        <View style={styles.dayHead} testID={`my-logs-day-${item.date}`}>
          <AppText variant="eyebrow">{dayTitle(item)}</AppText>
          <AppText variant="micro" style={styles.dayCount}>
            {t('myLogs.dayCount', {count: item.count})}
          </AppText>
        </View>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, today, yesterday],
  );

  let content: React.ReactNode;

  if (isError) {
    content = (
      <EmptyState
        tone="error"
        title={t('myLogs.error')}
        actionLabel={t('actions.retry')}
        onAction={refetch}
      />
    );
  } else if (isPending) {
    content = (
      <View style={[styles.list, insets]}>
        <SkeletonList count={7} avatar={false} />
      </View>
    );
  } else {
    content = (
      <FlatList
        testID="my-logs-feed"
        data={items}
        keyExtractor={item => `${item.kind}:${item.key}`}
        renderItem={renderItem}
        showsVerticalScrollIndicator
        contentContainerStyle={[
          styles.list,
          insets,
          items.length === 0 && styles.listEmpty,
        ]}
        /* Бесконечной ленты больше нет: период собирается целиком, догружать нечего.
           Осталась рейка, которая заодно говорит «из скольких». */
        ListHeaderComponent={<View style={styles.filters}>{filters}</View>}
        ListEmptyComponent={
          <EmptyState
            icon="logs"
            title={t(
              result || period ? 'myLogs.emptyFiltered' : 'myLogs.empty',
            )}
          />
        }
        ListFooterComponent={
          !isDesktop && totalPages > 1 ? (
            <Pager
              page={page}
              totalPages={totalPages}
              total={total}
              perPage={perPage}
              onPage={setPage}
              style={styles.footer}
              testID="my-logs-pager-phone"
            />
          ) : null
        }
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={refetch}
            tintColor={p.primary}
            colors={[p.primary]}
          />
        }
      />
    );
  }

  // Раму держит страница-хозяин: свой `Screen` здесь платил бы отступы и безопасную
  // зону во второй раз.
  return (
    <View style={styles.frame} testID="employee-logs">
      {content}
      {/* Рейка страниц — под лентой, где её видно, а не в конце прокрутки. На телефоне
          ленту листают пальцем, и рейка там была бы вторым, спорящим способом. */}
      {isDesktop ? (
        <Pager
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPage={setPage}
          perPageOptions={PAGE_SIZES}
          onPerPage={setPerPage}
          style={styles.pager}
          testID="my-logs-pager"
        />
      ) : null}
    </View>
  );
}
